import { AsyncSelectField } from "@components/forms/AsyncSelectField";
import { AlertTriangle, Plus, Route as RouteIcon, Terminal, X } from "lucide-react";
import { useCallback, useEffect } from "react";
import type { Control, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { Controller } from "react-hook-form";
import type { TicketFormData } from "../config/schemas";
import { type RouteSuggestion, useRouteSuggestions } from "../hooks/useRouteSuggestions";

interface TicketRouteSelectorProps {
  control: Control<TicketFormData>;
  watch: UseFormWatch<TicketFormData>;
  setValue: UseFormSetValue<TicketFormData>;
}

interface RouteEntry {
  route?: string | null;
  routeTitle?: string | null;
  apiEndpoint?: string | null;
  errorMessage?: string | null;
}

export function TicketRouteSelector({ control, watch, setValue }: TicketRouteSelectorProps) {
  const routeEntries = watch("routeEntries") ?? [];
  const routeSuggestions = useRouteSuggestions();

  const addEntry = useCallback(() => {
    const current = watch("routeEntries") ?? [];
    setValue(
      "routeEntries",
      [...current, { route: "", routeTitle: "", apiEndpoint: "", errorMessage: "" }],
      {
        shouldValidate: true,
      },
    );
  }, [watch, setValue]);

  const removeEntry = useCallback(
    (index: number) => {
      const current = watch("routeEntries") ?? [];
      setValue(
        "routeEntries",
        current.filter((_, i) => i !== index),
        { shouldValidate: true },
      );
    },
    [watch, setValue],
  );

  return (
    <div className="space-y-3">
      {/* Add button */}
      <button
        type="button"
        className="btn btn-outline btn-primary btn-sm gap-1.5 font-semibold"
        onClick={addEntry}
      >
        <Plus className="h-3.5 w-3.5" />
        Ajouter une route
      </button>

      {/* Route entries — connected timeline */}
      {routeEntries.length > 0 && (
        <div className="relative space-y-3 pl-1">
          {routeEntries.length > 1 && (
            <div className="absolute left-[15px] top-4 bottom-4 w-px bg-base-300" aria-hidden />
          )}
          {routeEntries.map((_entry: RouteEntry, index: number) => (
            <RouteEntryCard
              key={index}
              index={index}
              control={control}
              watch={watch}
              setValue={setValue}
              routeSuggestions={routeSuggestions}
              onRemove={() => removeEntry(index)}
              isConnected={routeEntries.length > 1}
            />
          ))}
        </div>
      )}

      {routeEntries.length === 0 && (
        <div className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-base-300 bg-base-200/30 px-4 py-6 text-center">
          <RouteIcon className="h-4 w-4 text-base-content/25" />
          <p className="text-xs text-base-content/40 italic">
            Aucune route ajoutée — cliquez sur « Ajouter une route » pour commencer.
          </p>
        </div>
      )}
    </div>
  );
}

function RouteEntryCard({
  index,
  control,
  watch,
  setValue,
  routeSuggestions,
  onRemove,
  isConnected,
}: {
  index: number;
  control: Control<TicketFormData>;
  watch: UseFormWatch<TicketFormData>;
  setValue: UseFormSetValue<TicketFormData>;
  routeSuggestions: RouteSuggestion[];
  onRemove: () => void;
  isConnected: boolean;
}) {
  const watchedRoute = watch(`routeEntries.${index}.route`);

  // Auto-fill routeTitle from selected route's title
  useEffect(() => {
    if (watchedRoute && typeof watchedRoute === "string" && watchedRoute.length > 0) {
      const match = routeSuggestions.find((r) => r.path === watchedRoute);
      if (match?.title) {
        setValue(`routeEntries.${index}.routeTitle`, match.title);
      }
    }
  }, [watchedRoute, routeSuggestions, index, setValue]);

  const fetchRoutes = useCallback(
    async (search: string) => {
      if (!search) return routeSuggestions;
      const lower = search.toLowerCase();
      return routeSuggestions.filter(
        (r) => r.path.toLowerCase().includes(lower) || r.title.toLowerCase().includes(lower),
      );
    },
    [routeSuggestions],
  );

  return (
    <div className="relative flex gap-3 animate-in fade-in slide-in-from-top-1 duration-200">
      {/* Timeline node */}
      {isConnected && (
        <span className="relative z-10 mt-3 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-base-100 ring-2 ring-base-300">
          <span className="text-[10px] font-mono font-bold text-base-content/50">{index + 1}</span>
        </span>
      )}

      <div className="min-w-0 flex-1 rounded-lg border border-base-300 bg-base-200/40 p-3 space-y-3 transition-colors duration-200 hover:border-primary/25 hover:bg-base-200/60">
        {/* Entry header */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-base-content/40">
            {isConnected ? `Route ${index + 1}` : "Détails de la route"}
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-xs btn-square text-base-content/35 hover:text-error hover:bg-error/10"
            onClick={onRemove}
            aria-label={`Supprimer la route ${index + 1}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Route autocomplete + Route title — 2 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <AsyncSelectField
            name={`routeEntries.${index}.route`}
            control={control}
            label="Route"
            fetchFn={fetchRoutes}
            getOptionLabel={(r) => `${r.path} — ${r.title}`}
            getOptionValue={(r) => r.path}
            placeholder="/rh/collaborateurs"
          />
          <RouteField
            control={control}
            name={`routeEntries.${index}.routeTitle`}
            label="Titre de la route"
            placeholder="Collaborateurs"
          />
        </div>

        {/* Error message + API endpoint — full width */}
        <RouteField
          control={control}
          name={`routeEntries.${index}.errorMessage`}
          label="Erreur associée"
          placeholder="500 Internal Server Error"
          icon={AlertTriangle}
          tone="error"
        />
        <RouteField
          control={control}
          name={`routeEntries.${index}.apiEndpoint`}
          label="Endpoint API"
          placeholder="GET /api/resource"
          icon={Terminal}
        />
      </div>
    </div>
  );
}

function RouteField({
  control,
  name,
  label,
  placeholder,
  icon: Icon,
  tone,
}: {
  control: Control<TicketFormData>;
  name: `routeEntries.${number}.${"route" | "routeTitle" | "apiEndpoint" | "errorMessage"}`;
  label: string;
  placeholder: string;
  icon?: typeof RouteIcon;
  tone?: "error";
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-base-content/50">
            {label} <span className="font-normal normal-case opacity-60">(optionnel)</span>
          </label>
          <div className="relative">
            {Icon && (
              <Icon
                className={`pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${
                  tone === "error" ? "text-error/50" : "text-base-content/30"
                }`}
              />
            )}
            <input
              type="text"
              className={`input input-bordered input-sm w-full bg-base-100 border-base-300 font-mono text-xs transition-all duration-200 focus:ring-2 ${
                Icon ? "pl-8" : ""
              } ${
                tone === "error"
                  ? "focus:border-error focus:ring-error/20"
                  : "focus:border-primary focus:ring-primary/20"
              }`}
              placeholder={placeholder}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          </div>
        </div>
      )}
    />
  );
}
